import type { WaypointBooking, TransportStatusEnum } from '@apiTypes'
import { TimeSlotBookingListStatusEnum } from '@apiTypes'
import i18n from '@/plugins/i18n'
import { isNullOrUndefined } from '@/utils/common.utils'
import { isTransportStatusFinished, isTransportStatusProcessing } from '@/views/transports/utils/transport.utils'

interface WaypointBookingStatus {
  booking: Pick<WaypointBooking, 'status'> | null
}

// What a transport cancel settles, and what a single booking can be canceled from: pending → withdrawn
// (a lapsed slot is still stored pending and shown as expired) and approved → canceled. Arrived and
// completed already happened; rejected, withdrawn, and canceled are already closed.
const CANCELLABLE_BOOKING_STATUSES = new Set<TimeSlotBookingListStatusEnum>([
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.APPROVED,
  TimeSlotBookingListStatusEnum.EXPIRED,
])

/** Bars that have not received a transport status yet keep bookings available until one arrives. */
export function isTransportBookingAllowed(status: TransportStatusEnum | null | undefined): boolean {
  return isNullOrUndefined(status) || isTransportStatusProcessing(status)
}

export function isTransportBookingActionable(status: TransportStatusEnum | null | undefined): boolean {
  return isTransportBookingAllowed(status) || isTransportStatusFinished(status)
}

export function canCancelBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return CANCELLABLE_BOOKING_STATUSES.has(status)
}

export function countCancellableBookings(waypoints: WaypointBookingStatus[]): number {
  return waypoints.filter(waypoint => !isNullOrUndefined(waypoint.booking) && canCancelBooking(waypoint.booking.status)).length
}

export function countCanceledBookings(waypoints: WaypointBookingStatus[]): number {
  return waypoints.filter(waypoint => waypoint.booking?.status === TimeSlotBookingListStatusEnum.CANCELED).length
}

// Literal `t('...')` arguments so vue-i18n-extract can see the keys. A constant passed through `t()` later is invisible.
export function transportCanceledBookingExplanation(): string {
  return i18n.global.t('Booking is not necessary because transport was canceled')
}

export function transportCancelBookingAlert(count: number): string | null {
  if (count <= 0)
    return null

  return i18n.global.t(
    'No linked bookings will be canceled. | {count} linked booking will be canceled. | {count} linked bookings will be canceled. | {count} linked bookings will be canceled.',
    { count },
    count,
  )
}

export function transportReopenBookingAlert(count: number): string | null {
  if (count <= 0)
    return null

  return i18n.global.t(
    'No canceled bookings need a separate follow-up. | {count} canceled booking will remain canceled and needs to be handled separately. | {count} canceled bookings will remain canceled and need to be handled separately. | {count} canceled bookings will remain canceled and need to be handled separately.',
    { count },
    count,
  )
}
